/**
 * ModuleConfigsAdmin — CRUD UI для preset_tier_module_config.
 *
 * Block A (search v3, 2026-09-22): UI перевёрнут под правило архитектуры A2.
 * Было: табы по тирам (economy/optimal/max), внутри — карточки модулей.
 * Стало: табы по ОПЕРАЦИЯМ («Поиск», «Сопоставление», «Генерация», «Перевод»,
 * «Медиа», «Утилиты»), внутри карточки операции — три тарифа (эконом /
 * оптималь / макс), и в каждом тарифе СВОЙ провайдер + СВОЯ модель + промпт
 * + параметры (включая rate-limit). Провайдер выбирается из справочника
 * ai_provider_config (useProviders) — раньше он был read-only.
 *
 * TZ 2026-09-23 (Блоки 2/3/5/6):
 * - тултипы ко всем числовым параметрам (InfoTip, подписи ×3 локалей);
 * - модель — динамический список моделей провайдера (/admin/providers/models)
 *   с поиском; у SerpAPI/DeepL поля модели нет;
 * - провайдер — выпадающий список ПРОВАЙДЕРОВ (optgroup по провайдеру,
 *   ключи — варианты выбора; runtime крутит ключи провайдера сам)
 *   + варианты «Карусель …» (params.llm_provider = 'carousel:<id>');
 *   провайдер без активных ключей — disabled + подпись «нет активных ключей»;
 * - у «Поиска» — два поля: «Модель (кто анализирует)» (provider_id+model_name)
 *   и «Источник ссылок» (params.search_source.provider); при источнике
 *   Gemini Grounding поле модели скрыто — источник определяет модель;
 * - «Перейти» из «Проверить всё»: слушатель cs:settings-jump переключает
 *   таб операции и подсвечивает целевую карточку.
 *
 * Фикс сохранения: раньше handleSave отправлял parseFloat() от полей как есть,
 * и при очищенном поле уходил NaN (невалидный JSON → 422 → «сохранение не
 * применяется»). Теперь в PATCH уходит ТОЛЬКО то, что реально изменилось и
 * валидно; пустые/нечисловые поля пропускаются, а не рушат payload.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Save,
  Loader2,
  Sliders,
  Search,
  Layers,
  FileText,
  Languages,
  ImageIcon,
  Wrench,
  AlertTriangle,
  ChevronDown,
} from 'lucide-react';

import {
  useAllModuleConfigs,
  useCarousels,
  useModules,
  useProviders,
  useProviderSummaries,
  useProviderModels,
  useUpdateModuleConfig,
  type AIProviderConfig,
  type AIModule,
  type KeyCarouselSet,
  type ModuleConfig,
  type ModuleConfigUpdate,
  type ProviderName,
  type ProviderSummary,
} from '@/hooks/useAdmin';
import { SearchBudgetEditor } from '@/components/admin/SearchBudgetEditor';
import { InfoTip } from '@/components/admin/InfoTip';
import { providerLabel } from '@/components/admin/providerMeta';
import {
  readSettingsJump,
  SETTINGS_JUMP_EVENT,
  type SettingsJumpTarget,
} from '@/components/admin/settingsJump';
import { cn } from '@/lib/utils';

type TierKey = 'economy' | 'optimal' | 'max' | 'super';

const TIERS: TierKey[] = ['economy', 'optimal', 'max', 'super'];

const TIER_BADGES: Record<TierKey, string> = {
  economy: 'bg-success-tint/10 text-success border-success-tint/20',
  optimal: 'bg-primary-500/10 text-primary-400 border-primary-500/20',
  max: 'bg-warning-tint/10 text-warning border-warning-tint/20',
  super: 'bg-fuchsia-500/10 text-fuchsia-400 border-fuchsia-500/20',
};

// Табы = типы задач (операции). Совпадение модуля с табом — по категории
// ai_module, кроме matching (в БД он utility, а логически это операция
// «Сопоставление» — отдельный таб по ТЗ).
type TabKey = 'search' | 'matching' | 'generation' | 'translation' | 'media' | 'utility';

const TABS: {
  key: TabKey;
  icon: typeof Search;
  match: (m: AIModule) => boolean;
}[] = [
  { key: 'search', icon: Search, match: (m) => m.category === 'search' },
  { key: 'matching', icon: Layers, match: (m) => m.key === 'matching' },
  { key: 'generation', icon: FileText, match: (m) => m.category === 'content' },
  { key: 'translation', icon: Languages, match: (m) => m.category === 'translation' },
  { key: 'media', icon: ImageIcon, match: (m) => m.category === 'media' },
  {
    key: 'utility',
    icon: Wrench,
    match: (m) => m.category === 'utility' && m.key !== 'matching',
  },
];

/** parseFloat без NaN-утечки: невалидное → null (поле пропускается при save). */
function parseNum(value: string): number | null {
  if (value === null || value === undefined || value === '') return null;
  const v = parseFloat(value);
  return Number.isFinite(v) ? v : null;
}

/** Провайдеры без концепции «модель» (TZ Блок 3) — поле модели скрыто. */
const NO_MODEL_PROVIDERS: ProviderName[] = ['serpapi', 'deepl'];

/** Источники поиска, где «источник определяет модель» — одно поле (TZ Блок 6). */
const SINGLE_FIELD_SOURCES = ['gemini_grounding'];

/** Классические источники ссылок + LLM-провайдеры как источник (params.search_source). */
const CLASSIC_SOURCE_OPTIONS = [
  'serpapi',
  'google_pse',
  'gemini_grounding',
] as const;

export function ModuleConfigsAdmin() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<TabKey>('search');
  const [jump, setJump] = useState<SettingsJumpTarget | null>(null);
  const { data: modules, isLoading: modulesLoading } = useModules();
  const { data: configs, isLoading: configsLoading } = useAllModuleConfigs();
  const { data: providers } = useProviders();
  const { data: summaries } = useProviderSummaries();
  const { data: carousels } = useCarousels({ staleTime: 60_000 });

  // «Перейти» из попапа «Проверить всё» (Блок 10): переключить таб операции
  // по карточке модуля и подсветить целевую карточку.
  useEffect(() => {
    const apply = (target: SettingsJumpTarget | null) => {
      if (!target || target.tab !== 'moduleConfigs' || !target.moduleKey) return;
      const mod = (modules ?? []).find((m) => m.key === target.moduleKey);
      if (mod) {
        const tb = TABS.find((t) => t.categories.includes(mod.category));
        setTab((tb ? tb.key : 'utility') as TabKey);
        setJump(target);
      }
    };
    apply(readSettingsJump());
    const handler = (e: Event) =>
      apply((e as CustomEvent<SettingsJumpTarget>).detail);
    window.addEventListener(SETTINGS_JUMP_EVENT, handler);
    return () => window.removeEventListener(SETTINGS_JUMP_EVENT, handler);
  }, [modules]);

  const tabModules = useMemo(() => {
    const def = TABS.find((tb) => tb.key === tab) ?? TABS[0];
    return (modules ?? []).filter(def.match);
  }, [tab, modules]);

  const configsByModule = useMemo(() => {
    const byModule = new Map<string, Map<TierKey, ModuleConfig>>();
    for (const cfg of configs ?? []) {
      let tiers = byModule.get(cfg.module_id);
      if (!tiers) {
        tiers = new Map<TierKey, ModuleConfig>();
        byModule.set(cfg.module_id, tiers);
      }
      tiers.set(cfg.tier_key, cfg);
    }
    return byModule;
  }, [configs]);

  const isLoading = modulesLoading || configsLoading;

  return (
    <div className="space-y-4">
      <p className="text-sm text-surface-400">
        {t('system.moduleConfigs.help', {
          defaultValue:
            'Для каждой операции и каждого тарифа — свой провайдер, модель, промпт и параметры.',
        })}
      </p>

      <div className="flex gap-1 border-b border-surface-800 overflow-x-auto">
        {TABS.map((tb) => (
          <button
            key={tb.key}
            onClick={() => setTab(tb.key)}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-2',
              tab === tb.key
                ? 'border-primary-500 text-primary-300'
                : 'border-transparent text-surface-400 hover:text-surface-200',
            )}
          >
            <tb.icon className="w-4 h-4" />
            {t(`system.moduleConfigs.tabs.${tb.key}`)}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-sm text-surface-400">Loading…</div>
      ) : tabModules.length === 0 ? (
        <div className="text-sm text-surface-400">
          {t('system.moduleConfigs.emptyTab', {
            defaultValue: 'Для этой операции ещё нет модулей.',
          })}
        </div>
      ) : (
        <div className="space-y-3">
          {tabModules.map((m) => (
            <ModuleCard
              key={m.id}
              module={m}
              tierConfigs={configsByModule.get(m.id)}
              providers={providers ?? []}
              summaries={summaries ?? []}
              carousels={carousels ?? []}
              jumpModuleKey={jump?.moduleKey ?? null}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ModuleCard({
  module,
  tierConfigs,
  providers,
  summaries,
  carousels,
  jumpModuleKey,
}: {
  module: AIModule;
  tierConfigs: Map<TierKey, ModuleConfig> | undefined;
  providers: AIProviderConfig[];
  summaries: ProviderSummary[];
  carousels: KeyCarouselSet[];
  jumpModuleKey: string | null;
}) {
  const highlighted = jumpModuleKey === module.key;
  return (
    <div
      data-cfg-card={module.key}
      className={cn(
        'card space-y-3',
        highlighted && 'ring-2 ring-primary-500/40 border-primary-500/50',
      )}
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-surface-800/60 flex items-center justify-center shrink-0">
          <Sliders className="w-5 h-5 text-surface-300" />
        </div>
        <div className="min-w-0 flex-1">
          <span className="font-semibold">{module.display_name_ru || module.key}</span>
          <div className="text-xs text-surface-500">{module.key}</div>
        </div>
      </div>

      {TIERS.map((tier) => {
        const cfg = tierConfigs?.get(tier);
        if (!cfg) return null;
        return (
          <TierBlock
            key={cfg.id}
            config={cfg}
            tier={tier}
            providers={providers}
            summaries={summaries}
            carousels={carousels}
          />
        );
      })}
    </div>
  );
}

function TierBlock({
  config,
  tier,
  providers,
  summaries,
  carousels,
}: {
  config: ModuleConfig;
  tier: TierKey;
  providers: AIProviderConfig[];
  summaries: ProviderSummary[];
  carousels: KeyCarouselSet[];
}) {
  const { t } = useTranslation();
  const update = useUpdateModuleConfig();
  const [draft, setDraft] = useState({
    provider_id: config.provider_id,
    model_name: config.model_name,
    system_prompt: config.system_prompt,
    temperature: config.temperature,
    top_p: config.top_p,
    frequency_penalty: config.frequency_penalty,
    max_tokens: String(config.max_tokens),
    input_price_per_1m: config.input_price_per_1m,
    output_price_per_1m: config.output_price_per_1m,
    is_unlocked: config.is_unlocked,
    requests_per_minute: rateLimitOf(config).requests_per_minute,
    pause_seconds: rateLimitOf(config).pause_seconds,
    search_engine_version: engineVersionOf(config),
  });
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);
  /* TZ Блоки 5/6: выбор «провайдер ИЛИ карусель» (llm_provider/search_source)
     — строка вида uuid ключа или 'carousel:<id>' */
  const [llmChoice, setLlmChoice] = useState<string>(() => llmChoiceOf(config));
  const [sourceChoice, setSourceChoice] = useState<string>(() =>
    sourceOf(config),
  );

  const setField = <K extends keyof typeof draft>(key: K, value: (typeof draft)[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setDirty(true);
    setSaved(false);
  };

  const chooseLlm = (v: string) => {
    setLlmChoice(v);
    setDirty(true);
    setSaved(false);
  };
  const chooseSource = (v: string) => {
    setSourceChoice(v);
    setDirty(true);
    setSaved(false);
  };

  const buildBody = (): ModuleConfigUpdate => {
    const body: ModuleConfigUpdate = {};
    // TZ Блок 5: карусель как источник LLM — params.llm_provider='carousel:<id>',
    // конкретный ключ одного из активных ключей провайдера — provider_id.
    const curLlm = llmChoice.trim();
    const isCarousel = curLlm.startsWith('carousel:');
    if (curLlm && curLlm !== llmChoiceOf(config)) {
      if (isCarousel) {
        body.params = mergeParams(config, { llm_provider: curLlm });
      } else {
        body.provider_id = firstKeyOfProvider(providers, curLlm) ?? '';
        if (llmChoiceOf(config).startsWith('carousel:')) {
          const prev = body.params ?? mergeParams(config, {});
          delete prev.llm_provider;
        }
      }
    }
    if (draft.model_name.trim() && draft.model_name.trim() !== config.model_name) {
      body.model_name = draft.model_name.trim();
    }
    if (draft.system_prompt !== config.system_prompt) {
      body.system_prompt = draft.system_prompt;
    }
    if (draft.is_unlocked !== config.is_unlocked) {
      body.is_unlocked = draft.is_unlocked;
    }

    // Числовые поля: невалидное/пустое значение молча пропускается (не NaN).
    const numericPairs: [keyof ModuleConfigUpdate, string, string][] = [
      ['temperature', draft.temperature, config.temperature],
      ['top_p', draft.top_p, config.top_p],
      ['frequency_penalty', draft.frequency_penalty, config.frequency_penalty],
      ['max_tokens', draft.max_tokens, String(config.max_tokens)],
      ['input_price_per_1m', draft.input_price_per_1m, config.input_price_per_1m],
      ['output_price_per_1m', draft.output_price_per_1m, config.output_price_per_1m],
    ];
    for (const [field, raw, original] of numericPairs) {
      const v = parseNum(raw);
      if (v === null) continue; // пустое/мусорное — не отправляем
      if (parseNum(original) !== v) {
        (body as Record<string, unknown>)[field] = v;
      }
    }

    // Rate-limit (Block A): params.rate_limit — только изменившиеся значения,
    // merged с существующим params (params на сервере заменяется целиком).
    const existing = rateLimitOf(config);
    const rpm = parseNum(draft.requests_per_minute);
    const pause = parseNum(draft.pause_seconds);
    const rpmChanged = rpm !== null ? rpm !== parseNum(existing.requests_per_minute) : false;
    const pauseChanged = pause !== null ? pause !== parseNum(existing.pause_seconds) : false;
    if (rpmChanged || pauseChanged) {
      const rl: Record<string, number> = {};
      if (rpm !== null) rl.requests_per_minute = rpm;
      if (pause !== null) rl.pause_seconds = pause;
      body.params = { ...(body.params ?? config.params ?? {}), rate_limit: rl };
    }

    // Engine switch (ТЗ v3): params.search_engine_version — только при изменении.
    if (draft.search_engine_version !== engineVersionOf(config)) {
      const params: Record<string, unknown> =
        (body.params as Record<string, unknown> | undefined) ??
        { ...(config.params ?? {}) };
      if (draft.search_engine_version) {
        params.search_engine_version = draft.search_engine_version;
      } else {
        delete params.search_engine_version;
      }
      body.params = params;
    }

    // TZ Блок 6: params.search_source — источник ссылок в карточке «Поиск».
    if (isSearchModule(config.module_key) && sourceChoice.trim() !== sourceOf(config)) {
      const params: Record<string, unknown> =
        (body.params as Record<string, unknown> | undefined) ??
        { ...(config.params ?? {}) };
      if (sourceChoice.trim()) {
        params.search_source = { provider: sourceChoice.trim() };
      } else {
        delete params.search_source;
      }
      body.params = params;
    }

    return body;
  };

  const handleSave = () => {
    const body = buildBody();
    if (Object.keys(body).length === 0) {
      // Ничего не изменилось (или всё невалидно) — просто снимаем dirty.
      setDirty(false);
      return;
    }
    update.mutate(
      { id: config.id, body },
      {
        onSuccess: () => {
          setDirty(false);
          setSaved(true);
        },
      },
    );
  };

  const searchMode = config.module_key === 'search_enrichment';
  const activeCarousels = carousels.filter((c) => c.is_active);
  // TZ Блок 6: при Gemini Grounding источник определяет модель — пара
  // провайдер/модель скрыта (одно поле вместо двух).
  const hideModelFields = searchMode && sourceChoice.trim() === 'gemini_grounding';
  // Провайдер текущего выбора — для списка моделей (карусель знает провайдера).
  const chosenCarousel = activeCarousels.find(
    (c) => `carousel:${c.id}` === llmChoice.trim() ||
      (searchMode && `carousel:${c.id}` === sourceChoice.trim()),
  );
  const modelListProvider = (
    chosenCarousel
      ? chosenCarousel.provider
      : llmChoice.trim()
  ) as ProviderName | '';
  const modelListSupportsModel = !NO_MODEL_PROVIDERS.includes(
    modelListProvider as ProviderName,
  );

  const errMsg = saveErrorText(update.error, t);

  return (
    <div
      className={cn(
        'rounded-xl border border-surface-800 p-3 space-y-3',
        update.isPending && 'opacity-70',
      )}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span
          className={cn(
            'text-xs px-2 py-0.5 rounded-md border uppercase',
            TIER_BADGES[tier],
          )}
        >
          {t(`system.moduleConfigs.tiers.${tier}`)}
        </span>
        {dirty && (
          <button
            onClick={handleSave}
            disabled={update.isPending}
            className="btn-primary text-sm flex items-center gap-2"
          >
            {update.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {t('system.moduleConfigs.save', { defaultValue: 'Сохранить' })}
          </button>
        )}
        {saved && !dirty && !update.isPending && (
          <span className="text-xs text-success">
            {t('system.moduleConfigs.saved', { defaultValue: 'Сохранено' })}
          </span>
        )}
        {errMsg && (
          <span className="text-xs text-danger flex items-center gap-1">
            <AlertTriangle className="w-3.5 h-3.5" />
            {errMsg}
          </span>
        )}
      </div>

      {isSearchModule(config.module_key) && (
        <div>
          <label className="text-xs text-surface-500 uppercase tracking-wider flex items-center gap-1.5">
            {t('system.moduleConfigs.fields.searchSource', {
              defaultValue: 'Источник ссылок',
            })}
          </label>
          <select
            className="input w-full mt-1"
            value={sourceChoice}
            onChange={(e) => chooseSource(e.target.value)}
          >
            {CLASSIC_SOURCE_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {t(`system.moduleConfigs.searchSources.${s}`, { defaultValue: s })}
              </option>
            ))}
            {activeCarousels.map((c) => (
              <option key={c.id} value={`carousel:${c.id}`}>
                {t('system.moduleConfigs.searchSources.carousel', {
                  defaultValue: 'Карусель',
                })}
                : {c.name}
              </option>
            ))}
          </select>
          {isSingleFieldSource && (
            <div className="text-xs text-surface-500 mt-1">
              {t('system.moduleConfigs.fields.geminiGroundingNote', {
                defaultValue:
                  'Gemini Grounding: источник определяет модель — модель и поиск одна сущность.',
              })}
            </div>
          )}
        </div>
      )}

      {!hideModelFields && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-surface-500 uppercase tracking-wider">
              {t('system.moduleConfigs.fields.provider', { defaultValue: 'Провайдер' })}
            </label>
          <ProviderSelect
            providers={providers}
            summaries={summaries}
            carousels={activeCarousels}
            value={llmChoice}
            onChange={(v) => chooseLlm(v)}
          />
        </div>

        <div>
          <label className="text-xs text-surface-500 uppercase tracking-wider flex items-center gap-1.5">
            {t('system.moduleConfigs.fields.model', { defaultValue: 'Модель' })}
            <InfoTip text={t('system.moduleConfigs.tips.model')} />
          </label>
          <ModelPicker
            providerName={modelListProvider}
            supportsModel={modelListSupportsModel}
            value={draft.model_name}
            onChange={(v) => setField('model_name', v)}
            searchPlaceholder={t('system.moduleConfigs.fields.searchModel', {
              defaultValue: 'Поиск по моделям…',
            })}
          />
        </div>
      </div>
      )}

      <details className="group">
        <summary className="text-xs text-surface-500 uppercase tracking-wider cursor-pointer select-none">
          {t('system.moduleConfigs.fields.systemPrompt', { defaultValue: 'Системный промпт' })}
        </summary>
        <textarea
          className="input w-full mt-1 min-h-[80px] font-mono text-sm"
          value={draft.system_prompt}
          onChange={(e) => setField('system_prompt', e.target.value)}
        />
      </details>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <NumberField
          label={t('system.moduleConfigs.fields.temperature', { defaultValue: 'Temperature' })}
          tip={t('system.moduleConfigs.tips.temperature')}
          value={draft.temperature}
          step={0.05}
          min={0}
          max={2}
          onChange={(v) => setField('temperature', String(v))}
        />
        <NumberField
          label={t('system.moduleConfigs.fields.topP', { defaultValue: 'Top P' })}
          tip={t('system.moduleConfigs.tips.topP')}
          value={draft.top_p}
          step={0.05}
          min={0}
          max={1}
          onChange={(v) => setField('top_p', String(v))}
        />
        <NumberField
          label={t('system.moduleConfigs.fields.freqPenalty', { defaultValue: 'Freq penalty' })}
          tip={t('system.moduleConfigs.tips.freqPenalty')}
          value={draft.frequency_penalty}
          step={0.05}
          min={-2}
          max={2}
          onChange={(v) => setField('frequency_penalty', String(v))}
        />
        <NumberField
          label={t('system.moduleConfigs.fields.maxTokens', { defaultValue: 'Max tokens' })}
          tip={t('system.moduleConfigs.tips.maxTokens')}
          value={draft.max_tokens}
          step={100}
          min={100}
          max={8000}
          onChange={(v) => setField('max_tokens', String(Math.round(v)))}
        />
        <NumberField
          label={t('system.moduleConfigs.fields.inputPrice', { defaultValue: 'Вход $/1M' })}
          tip={t('system.moduleConfigs.tips.inputPrice')}
          value={draft.input_price_per_1m}
          step={0.1}
          min={0}
          max={100}
          onChange={(v) => setField('input_price_per_1m', String(v))}
        />
        <NumberField
          label={t('system.moduleConfigs.fields.outputPrice', { defaultValue: 'Выход $/1M' })}
          tip={t('system.moduleConfigs.tips.outputPrice')}
          value={draft.output_price_per_1m}
          step={0.1}
          min={0}
          max={100}
          onChange={(v) => setField('output_price_per_1m', String(v))}
        />
      </div>

      <div className="grid grid-cols-2 gap-3 md:max-w-md">
        <NumberField
          label={t('system.moduleConfigs.fields.requestsPerMinute', {
            defaultValue: 'Запросов в минуту',
          })}
          tip={t('system.moduleConfigs.tips.requestsPerMinute')}
          value={draft.requests_per_minute}
          step={1}
          min={0}
          max={600}
          onChange={(v) => setField('requests_per_minute', String(Math.round(v)))}
        />
        <NumberField
          label={t('system.moduleConfigs.fields.pauseSeconds', {
            defaultValue: 'Пауза между запросами, сек',
          })}
          tip={t('system.moduleConfigs.tips.pauseSeconds')}
          value={draft.pause_seconds}
          step={0.1}
          min={0}
          max={60}
          onChange={(v) => setField('pause_seconds', String(v))}
        />
      </div>

      <label className="flex items-center gap-2 text-sm text-surface-300">
        <input
          type="checkbox"
          checked={draft.is_unlocked}
          onChange={(e) => setField('is_unlocked', e.target.checked)}
        />
        {t('system.moduleConfigs.fields.unlocked', { defaultValue: 'Доступен тарифам' })}
      </label>

      {/* ТЗ v3: переключатель поискового движка (search_enrichment only). */}
      {config.module_key === 'search_enrichment' && (
        <div className="md:max-w-xs">
          <label className="text-xs text-surface-500 uppercase tracking-wider">
            {t('system.moduleConfigs.fields.engineVersion', {
              defaultValue: 'Версия поискового движка',
            })}
          </label>
          <select
            className="input w-full mt-1"
            value={draft.search_engine_version}
            onChange={(e) => setField('search_engine_version', e.target.value)}
          >
            <option value="">
              {t('system.moduleConfigs.fields.engineDefault', {
                defaultValue: 'v2 (по умолчанию)',
              })}
            </option>
            <option value="v2">v2</option>
            <option value="v3">
              {t('system.moduleConfigs.fields.engineV3', {
                defaultValue: 'v3 (LLM-поиск)',
              })}
            </option>
          </select>
        </div>
      )}

      {/* Block S2: per-tier search budget override (only for search_enrichment). */}
      {config.module_key === 'search_enrichment' && (
        <SearchBudgetEditor config={config} />
      )}
    </div>
  );
}

function engineVersionOf(config: ModuleConfig): string {
  const v = (config.params ?? {}).search_engine_version;
  return typeof v === 'string' ? v : '';
}

function rateLimitOf(config: ModuleConfig): {
  requests_per_minute: string;
  pause_seconds: string;
} {
  const rl = (config.params ?? {}).rate_limit as
    | { requests_per_minute?: number; pause_seconds?: number }
    | undefined;
  return {
    requests_per_minute: rl?.requests_per_minute != null ? String(rl.requests_per_minute) : '',
    pause_seconds: rl?.pause_seconds != null ? String(rl.pause_seconds) : '',
  };
}

function saveErrorText(
  error: unknown,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string | null {
  if (!error) return null;
  const err = error as { response?: { data?: { detail?: unknown } }; message?: string };
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  return t('system.moduleConfigs.errors.saveFailed', {
    defaultValue: 'Не удалось сохранить. Проверь значения.',
  });
}

function NumberField({
  label,
  tip,
  value,
  step,
  min,
  max,
  onChange,
}: {
  label: string;
  tip?: string;
  value: string;
  step: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
}) {
  return (
    <div>
      <label className="text-xs text-surface-500 uppercase tracking-wider flex items-center gap-1.5">
        {label}
        {tip && <InfoTip text={tip} />}
      </label>
      <input
        type="number"
        step={step}
        min={min}
        max={max}
        className="input w-full mt-1 text-right"
        value={value}
        onChange={(e) => {
          const n = parseFloat(e.target.value);
          if (Number.isFinite(n)) onChange(n);
        }}
      />
    </div>
  );
}
// === TZ Блоки 3/5/6: хелперы ===

/** Выбор источника LLM: 'carousel:<id>' если задан, иначе имя провайдера. */
function llmChoiceOf(config: ModuleConfig): string {
  const ref = (config.params ?? {}).llm_provider;
  if (typeof ref === 'string' && ref.startsWith('carousel:')) return ref;
  return config.provider_name || '';
}

/** Источник ссылок (params.search_source.provider) — '' если не задан. */
function sourceOf(config: ModuleConfig): string {
  const src = (config.params ?? {}).search_source;
  if (src && typeof src === 'object' && typeof (src as Record<string, unknown>).provider === 'string') {
    return String((src as Record<string, unknown>).provider);
  }
  return '';
}

/** params с заменой/удалением поля (params на сервере меняется целиком). */
function mergeParams(
  config: ModuleConfig,
  patch: Record<string, string | undefined>,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...(config.params ?? {}) };
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined) delete out[k];
    else out[k] = v;
  }
  return out;
}

/** id «представительского» ключа провайдера (default первым или первым) —
 *  backend резолвит по нему имя провайдера и крутит ВСЕ его активные ключи. */
function firstKeyOfProvider(
  providers: AIProviderConfig[],
  providerName: string,
): string | null {
  const keys = providers.filter(
    (p) => p.provider === providerName && p.status !== 'disabled',
  );
  keys.sort((a, b) => Number(b.is_default) - Number(a.is_default));
  return keys.length > 0 ? keys[0].id : null;
}

function isSearchModule(key: string): boolean {
  return key === 'search_enrichment';
}

// === TZ Блок 3: ProviderSelect — список провайдеров + карусели ===

type ProviderOption = {
  value: string;
  label: string;
  disabled: boolean;
};

function ProviderSelect({
  providers,
  summaries,
  carousels,
  value,
  onChange,
}: {
  providers: AIProviderConfig[];
  summaries: ProviderSummary[];
  carousels: KeyCarouselSet[];
  value: string;
  onChange: (v: string) => void;
}) {
  const { t } = useTranslation();

  const options = useMemo(() => {
    const summaryOf = new Map(
      summaries.map((s) => [s.provider as string, s] as const),
    );
    const groups = new Map<string, AIProviderConfig[]>();
    for (const p of providers) {
      const arr = groups.get(p.provider) ?? [];
      arr.push(p);
      groups.set(p.provider, arr);
    }
    const out: { provider: string; label: string; hasKeys: boolean; keys: AIProviderConfig[] }[] = [];
    for (const [provider, keys] of groups) {
      const s = summaryOf.get(provider);
      const label = providerLabel(provider);
      const hasKeys = s ? s.has_active_keys : keys.some((k) => k.status === 'active');
      out.push({ provider, label, hasKeys, keys });
    }
    out.sort((a, b) => Number(b.hasKeys) - Number(a.hasKeys) || a.label.localeCompare(b.label));
    return out;
  }, [providers, summaries]);

  const noKeysLabel = t('system.moduleConfigs.providerSelect.noKeys', {
    defaultValue: 'нет активных ключей',
  });
  const carouselLabel = t('system.moduleConfigs.providerSelect.carousels', {
    defaultValue: 'Карусели',
  });

  return (
    <select className="input w-full mt-1" value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map((g) => (
        <optgroup
          key={g.provider}
          label={g.hasKeys ? g.label : `${g.label} — ${noKeysLabel}`}
          disabled={!g.hasKeys}
        >
          {g.keys.map((k) => (
            <option
              key={k.id}
              value={g.provider}
              disabled={!g.hasKeys}
            >
              {k.display_name}
              {k.status !== 'active'
                ? ` — ${t(`system.moduleConfigs.providerStatus.${k.status}`)}`
                : ''}
            </option>
          ))}
        </optgroup>
      ))}
      {carousels.length > 0 && (
        <optgroup label={carouselLabel}>
          {carousels.map((c) => (
            <option key={c.id} value={`carousel:${c.id}`}>
              {t('system.moduleConfigs.providerSelect.carouselName', {
                defaultValue: 'Карусель',
              })}
              : {c.name}
              {c.is_active ? '' : ' (off)'}
            </option>
          ))}
        </optgroup>
      )}
    </select>
  );
}

// === TZ Блок 3: ModelPicker — динамический список моделей провайдера с поиском ===

function ModelPicker({
  providerName,
  supportsModel,
  value,
  onChange,
  searchPlaceholder,
}: {
  providerName: ProviderName | '';
  supportsModel: boolean;
  value: string;
  onChange: (v: string) => void;
  searchPlaceholder: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const boxRef = useRef<HTMLDivElement>(null);

  // Запрашиваем каталог только при открытом списке (и только для
  // провайдеров с концепцией модели).
  const enabled = open && supportsModel && providerName !== '';
  const { data, isFetching, error } = useProviderModels(enabled ? providerName : null);
  const items = data?.items ?? [];

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items.slice(0, 80);
    const hits = items.filter(
      (m) =>
        m.id.toLowerCase().includes(needle) ||
        (m.name ?? '').toLowerCase().includes(needle),
    );
    return hits.slice(0, 80);
  }, [items, q]);

  if (!supportsModel) {
    return (
      <div>
        <input
          type="text"
          className="input w-full mt-1"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        <div className="text-xs text-surface-500 mt-1">
          {t('system.moduleConfigs.modelPicker.noModel', {
            defaultValue: 'У этого провайдера нет моделей — поле не используется.',
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="relative" ref={boxRef}>
      <div className="flex gap-1 items-center">
        <input
          type="text"
          className="input w-full mt-1"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={searchPlaceholder}
          onFocus={() => setOpen(true)}
        />
        <button
          type="button"
          aria-label={t('system.moduleConfigs.modelPicker.open', { defaultValue: 'Список моделей' })}
          className="btn-ghost mt-1 px-2 shrink-0"
          onClick={() => setOpen((o) => !o)}
        >
          <ChevronDown className="w-4 h-4" />
        </button>
      </div>
      {open && (
        <div className="absolute z-30 mt-1 w-full max-h-72 overflow-auto rounded-xl border border-surface-700 bg-surface-900 shadow-lg">
          <input
            type="text"
            className="input w-full rounded-none border-0 border-b border-surface-700 text-sm"
            placeholder={searchPlaceholder}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            autoFocus
          />
          {isFetching && !data && (
            <div className="px-3 py-2 text-xs text-surface-400">…</div>
          )}
          {error && (
            <div className="px-3 py-2 text-xs text-warning">
              {t('system.moduleConfigs.modelPicker.error', {
                defaultValue: 'Список недоступен — можно ввести модель вручную.',
              })}
            </div>
          )}
          {!isFetching && !error && items.length === 0 && (
            <div className="px-3 py-2 text-xs text-surface-500">
              {t('system.moduleConfigs.modelPicker.empty', {
                defaultValue: 'Моделей не получено — можно ввести вручную.',
              })}
            </div>
          )}
          {filtered.map((m) => (
            <button
              key={m.id}
              type="button"
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-primary-500/10 flex items-center justify-between gap-2"
              onClick={() => {
                onChange(m.id);
                setOpen(false);
                setQ('');
              }}
            >
              <span className="font-mono truncate">{m.id}</span>
              <span className="text-xs text-surface-500 truncate flex items-center gap-1">
                {m.is_free && (
                  <span className="text-[10px] px-1 rounded bg-success-tint/10 text-success border border-success-tint/20">
                    free
                  </span>
                )}
                {m.name && m.name !== m.id && <span className="truncate">{m.name}</span>}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
